import { MdsConfig } from '@maersk-global/mds-config';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ThemeContext } from '../contexts/ThemeContext';
import type { ThemeContextState, ThemeVariant } from '../contexts/ThemeContext';

import '@maersk-global/fonts/maeu/fonts.css';
import '@maersk-global/mds-design-tokens/alianca/light/css/design-tokens-px.css';
import '@maersk-global/mds-foundations/css/foundations.css';

import '../styles/styles.css';

MdsConfig.iconsDynamicImportPath = import.meta.env.APP_ASSETS_PATH;

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [themeVariant, setThemeVariant] = useState(() => {
    const stored = localStorage.getItem('[mds-theme-switch]theme') ?? 'light';
    return (stored ?? '').replace(/"/g, '') as ThemeVariant;
  });

  const darkThemeElementRef = useRef<HTMLElement | null>(null);

  const toggleThemeVariant = useCallback(() => {
    setThemeVariant((previous) => (previous === 'light' ? 'dark' : 'light'));
  }, []);

  useLayoutEffect(() => {
    const loadDarkStyles = async () => {
      await import('@maersk-global/mds-design-tokens/alianca/dark/css/design-tokens-px.css');

      darkThemeElementRef.current =
        document.querySelector('style[data-vite-dev-id*="dark/css/design-tokens"]') ??
        document.querySelector('link[href*="assets/dark-design-tokens-px"]');
    };

    if (themeVariant === 'dark') {
      if (darkThemeElementRef.current) {
        document.head.appendChild(darkThemeElementRef.current);
      } else {
        loadDarkStyles();
      }
      document.body.setAttribute('data-theme', 'dark');
    } else {
      if (darkThemeElementRef.current) {
        darkThemeElementRef.current.remove();
      }
      document.body.removeAttribute('data-theme');
    }
  }, [themeVariant]);

  const contextValue: ThemeContextState = useMemo(
    () => ({ themeVariant, toggleThemeVariant }),
    [themeVariant, toggleThemeVariant]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
